import { getFighterProfile } from "@/data/profiles";
import { getEquipmentById, getWeaponById } from "@/data";
import type { TrainingTier } from "@/types/game-data";
import type { ModelGroup } from "@/utils/grouping";

interface SoldierStatCardProps {
	group: ModelGroup;
	elementName: string;
}

export function SoldierStatCard({ group, elementName }: SoldierStatCardProps) {
	const profile = getFighterProfile(group.tier as TrainingTier);
	const weapons = group.weaponIds.map((id) => getWeaponById(id)).filter(Boolean);
	const equipment = group.equipmentIds.map((id) => getEquipmentById(id)).filter(Boolean);

	// Separate ranged and assault weapons
	const rangedWeapons = weapons.filter((w) => w!.stats);
	const assaultWeapons = weapons.filter((w) => w!.assaultStats);
	const otherWeapons = weapons.filter((w) => !w!.stats && !w!.assaultStats);

	return (
		<div className="print-stat-card border border-gray-400 rounded-lg p-3 mb-3 bg-white break-inside-avoid">
			{/* Header */}
			<div className="flex items-center justify-between border-b border-gray-300 pb-2 mb-2">
				<div>
					<span className="font-bold text-sm text-gray-900">{group.label}</span>
					<span className="text-xs text-gray-500 ml-2">— {elementName}</span>
				</div>
			</div>

			{/* Fighter Profile Stats */}
			<div className="flex gap-2 mb-2">
				{[
					{ label: "M", value: `${profile.movement}"` },
					{ label: "B", value: profile.ballistics },
					{ label: "A", value: profile.awareness },
					{ label: "S", value: profile.stress },
				].map((stat) => (
					<div
						key={stat.label}
						className="flex flex-col items-center border border-gray-300 rounded px-2 py-1 min-w-[2.5rem]"
					>
						<span className="text-[10px] font-bold text-gray-500 uppercase">{stat.label}</span>
						<span className="text-sm font-bold text-gray-900">{stat.value}</span>
					</div>
				))}
			</div>

			{/* Ranged Weapon Table */}
			{rangedWeapons.length > 0 && (
				<div className="mb-2 overflow-x-auto">
					<table className="w-full text-[10px] border-collapse">
						<thead>
							<tr className="bg-gray-100">
								<th className="text-left px-1 py-0.5 border border-gray-300 font-bold">Weapon</th>
								<th colSpan={4} className="text-center px-1 py-0.5 border border-gray-300 font-bold">CQB</th>
								<th colSpan={4} className="text-center px-1 py-0.5 border border-gray-300 font-bold">Engagement</th>
								<th colSpan={4} className="text-center px-1 py-0.5 border border-gray-300 font-bold">Extreme</th>
								<th className="text-left px-1 py-0.5 border border-gray-300 font-bold">Special</th>
							</tr>
							<tr className="bg-gray-50">
								<th className="border border-gray-300 px-1"></th>
								{["R", "M", "L", "AP"].map((h) => (
									<th key={`cqb-${h}`} className="text-center px-0.5 border border-gray-300 font-medium text-gray-600">{h}</th>
								))}
								{["R", "M", "L", "AP"].map((h) => (
									<th key={`eng-${h}`} className="text-center px-0.5 border border-gray-300 font-medium text-gray-600">{h}</th>
								))}
								{["R", "M", "L", "AP"].map((h) => (
									<th key={`ext-${h}`} className="text-center px-0.5 border border-gray-300 font-medium text-gray-600">{h}</th>
								))}
								<th className="border border-gray-300 px-1"></th>
							</tr>
						</thead>
						<tbody>
							{rangedWeapons.map((w) => {
								const s = w!.stats!;
								return (
									<tr key={w!.id}>
										<td className="px-1 py-0.5 border border-gray-300 font-medium text-gray-900 whitespace-nowrap">{w!.name}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.modifier != null ? (s.cqb.modifier >= 0 ? `+${s.cqb.modifier}` : s.cqb.modifier) : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.cqb.ap != null ? s.cqb.ap : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.modifier != null ? (s.engagement.modifier >= 0 ? `+${s.engagement.modifier}` : s.engagement.modifier) : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.engagement.ap != null ? s.engagement.ap : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.modifier != null ? (s.extreme.modifier >= 0 ? `+${s.extreme.modifier}` : s.extreme.modifier) : "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{s.extreme.ap != null ? s.extreme.ap : "-"}</td>
										<td className="px-1 py-0.5 border border-gray-300 text-gray-700">{s.specialRules.join(", ") || "-"}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Assault Weapon Table */}
			{assaultWeapons.length > 0 && (
				<div className="mb-2">
					<table className="w-full text-[10px] border-collapse">
						<thead>
							<tr className="bg-gray-100">
								<th className="text-left px-1 py-0.5 border border-gray-300 font-bold">Melee Weapon</th>
								<th className="text-center px-1 py-0.5 border border-gray-300 font-bold">R</th>
								<th className="text-center px-1 py-0.5 border border-gray-300 font-bold">L</th>
								<th className="text-center px-1 py-0.5 border border-gray-300 font-bold">AP</th>
								<th className="text-left px-1 py-0.5 border border-gray-300 font-bold">Special</th>
							</tr>
						</thead>
						<tbody>
							{assaultWeapons.map((w) => {
								const a = w!.assaultStats!;
								return (
									<tr key={w!.id}>
										<td className="px-1 py-0.5 border border-gray-300 font-medium text-gray-900">{w!.name}</td>
										<td className="text-center px-0.5 border border-gray-300">{a.range}</td>
										<td className="text-center px-0.5 border border-gray-300">{a.lethality ?? "-"}</td>
										<td className="text-center px-0.5 border border-gray-300">{a.ap != null ? a.ap : "-"}</td>
										<td className="px-1 py-0.5 border border-gray-300 text-gray-700">{a.specialRules.join(", ") || "-"}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Other weapons without stats (thrown, ammo, etc.) */}
			{otherWeapons.length > 0 && (
				<div className="mb-2">
					<div className="text-[10px] text-gray-600">
						<span className="font-bold">Other: </span>
						{otherWeapons.map((w) => w!.name).join(", ")}
					</div>
				</div>
			)}

			{/* Equipment List */}
			{equipment.length > 0 && (
				<div className="border-t border-gray-200 pt-1">
					<div className="text-[10px] font-bold text-gray-600 mb-0.5">Equipment</div>
					<div className="grid gap-0.5">
						{equipment.map((e) => (
							<div key={e!.id} className="text-[10px]">
								<span className="font-medium text-gray-900">{e!.name}</span>
								{e!.rules && (
									<span className="text-gray-500 ml-1">— {e!.rules}</span>
								)}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
